import React, { useEffect, useState } from 'react';
import { Alert, Badge, Card, Col, Container, Row } from 'react-bootstrap';
import {
  FaCalendarAlt,
  FaCheckCircle,
  FaClipboardList,
  FaHourglassHalf,
  FaRedo,
  FaSpinner,
  FaTachometerAlt
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import DuserAuditProgress from '../../../Components/JBranchDuserAuditProgress';



function JBranchDuserDashboard() {
  const [dashboardStats, setDashboardStats] = useState(null);
  const [reauditStats, setReauditStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const navigate = useNavigate();

  // Get user data from localStorage
  const userData = JSON.parse(localStorage.getItem('dUser'));
  const BranchCode = userData?.BranchCode;
  const department = userData?.department;
  const userName = userData?.firstName;
  const userId = userData?.id;
  
  const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8081';
  
  const currentDate = new Date().toLocaleDateString('en-US', { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  useEffect(() => {
    if (BranchCode && department && userId) {
      fetchDashboardStats();
      fetchUserProfile();
    } else {
      setError("Department, branch, or user information missing");
      setLoading(false);
    }
  }, [BranchCode, department, userId]);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      
      // Fetch normal audit stats
      const normalResponse = await fetch(
        `${BASE_URL}/get-jbranch-department-dashboard-stats/${BranchCode}/${department}`
      );
      
      // Fetch re-audit stats
      const reauditResponse = await fetch(
        `${BASE_URL}/get-jbranch-department-reaudit-dashboard-stats/${BranchCode}/${department}`
      );

      // Fetch combined stats
      const combinedResponse = await fetch(
        `${BASE_URL}/get-jbranch-department-combined-dashboard-stats/${BranchCode}/${department}`
      );
      
      if (!normalResponse.ok || !reauditResponse.ok || !combinedResponse.ok) {
        throw new Error('Failed to fetch dashboard statistics');
      }
      
      const normalData = await normalResponse.json();
      const reauditData = await reauditResponse.json();
      const combinedData = await combinedResponse.json();
      
      if (normalData.success) {
        setDashboardStats(normalData.stats);
        
        // Check if user is part of a factory
        if (normalData.hasFactoryCode) {
          console.log(`User is part of factory: ${normalData.factoryCode}`);
        }
      } else {
        console.error("Failed to fetch normal audit stats:", normalData.error);
      }
      
      if (reauditData.success) {
        setReauditStats(reauditData.stats);
      } else {
        console.error("Failed to fetch re-audit stats:", reauditData.error);
      }

      
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserProfile = async () => {
    try {
      const response = await fetch(`${BASE_URL}/get-department-user/${userId}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch user profile');
      }
      
      const data = await response.json();
      
      if (data.success) {
        setUserProfile(data.user);
      } else {
        console.error("Failed to fetch user profile:", data.error);
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  };

  const handleCardClick = (status, type = 'normal') => {
    // Navigate to the appropriate audit details page with the status as query parameter
    if (type === 'reaudit') {
      navigate(`/JBranchDuserReAudit?tab=${status}`);
    } else {
      navigate(`/JBranchDuserAudit?tab=${status}`);
    }
  };

  const getDepartmentInitials = () => {
    if (!userName) return "DU";
    return userName.split(' ').map(word => word.charAt(0)).join('').toUpperCase().substring(0, 2);
  };

  return (
    <Container fluid className="p-0">
      {/* Dashboard Header */}
      <div className="dashboard-header bg-primary text-white">
        <Container fluid className="px-4 py-4">
          <Row className="align-items-center">
            <Col lg={6}>
              <div className="d-flex align-items-center">
                <div className="avatar-container me-3">
                  {userProfile?.profileImage ? (
                    <>
                      <img 
                        src={`${BASE_URL}${userProfile.profileImage}`}
                        alt={`${userName || 'User'} Avatar`}
                        className="department-avatar-img"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div className="department-avatar" style={{ display: 'none' }}>
                        {getDepartmentInitials()}
                      </div>
                    </>
                  ) : (
                    <div className="department-avatar">
                      {getDepartmentInitials()}
                    </div>
                  )}
                </div>
                <div>
                  <h4 className="greeting mb-0">Welcome, {userName || 'Department User'}!</h4>
                  <p className="mb-0 opacity-75">
                    <FaCalendarAlt className="me-1" size={14} /> {currentDate}
                  </p>
                </div>
              </div>
            </Col>
            <Col lg={6} className="text-lg-end mt-3 mt-lg-0">
              <Badge bg="white" text="primary" className="stats-badge">
                <FaTachometerAlt className="me-1" /> Department Dashboard Overview
              </Badge>
            </Col>
          </Row>
        </Container>
      </div>
      
      {/* Dashboard Content */}
      <Container fluid className="px-4 py-4 dashboard-content">
        {error && (
          <Alert variant="danger" className="mb-4">
            <Alert.Heading><FaCheckCircle className="me-2" /> Error Loading Dashboard</Alert.Heading>
            <p className="mb-0">{error}</p>
          </Alert>
        )}

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-grow text-primary mb-3" style={{ width: '3rem', height: '3rem' }} role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-primary fw-bold mb-1">Loading Dashboard</p>
            <p className="text-muted">Please wait while we fetch department stats...</p>
          </div>
        ) : (
          <>
            {/* Normal Audits Section */}
            {dashboardStats && (
              <>
                <Row className="g-4 mb-4">
                  <Col xs={12}>
                    <h5 className="section-title mb-3">
                      <FaClipboardList className="me-2" /> Audits
                    </h5>
                  </Col>

                  {/* Total Audits Card */}
                  <Col xs={12} sm={6} md={6} lg={3}>
                    <Card className="stat-card border-0 shadow-sm h-100" onClick={() => handleCardClick('all', 'normal')}>
                      <Card.Body>
                        <div className="d-flex align-items-center mb-3">
                          <div className="stat-icon audit-icon">
                            <FaClipboardList />
                          </div>
                          <h5 className="stat-label ms-3 mb-0">Total Audits</h5>
                        </div>
                        <h3 className="stat-value">{dashboardStats.total}</h3>
                      </Card.Body>
                    </Card>
                  </Col>

                  {/* Pending Audits Card */}
                  <Col xs={12} sm={6} md={6} lg={3}>
                    <Card className="stat-card border-0 shadow-sm h-100" onClick={() => handleCardClick('pending', 'normal')}>
                      <Card.Body>
                        <div className="d-flex align-items-center mb-3">
                          <div className="stat-icon pending-icon">
                            <FaHourglassHalf />
                          </div>
                          <h5 className="stat-label ms-3 mb-0">Pending</h5>
                        </div>
                        <h3 className="stat-value">{dashboardStats.pending}</h3>
                      </Card.Body>
                    </Card>
                  </Col>

                  {/* In Progress Audits Card */}
                  <Col xs={12} sm={6} md={6} lg={3}>
                    <Card className="stat-card border-0 shadow-sm h-100" onClick={() => handleCardClick('inProgress', 'normal')}>
                      <Card.Body>
                        <div className="d-flex align-items-center mb-3">
                          <div className="stat-icon progress-icon">
                            <FaSpinner />
                          </div>
                          <h5 className="stat-label ms-3 mb-0">In Progress</h5>
                        </div>
                        <h3 className="stat-value">{dashboardStats.inProgress}</h3>
                      </Card.Body>
                    </Card>
                  </Col>

                  {/* Completed Audits Card */}
                  <Col xs={12} sm={6} md={6} lg={3}>
                    <Card className="stat-card border-0 shadow-sm h-100" onClick={() => handleCardClick('completed', 'normal')}>
                      <Card.Body>
                        <div className="d-flex align-items-center mb-3">
                          <div className="stat-icon completed-icon">
                            <FaCheckCircle />
                          </div>
                          <h5 className="stat-label ms-3 mb-0">Completed</h5>
                        </div>
                        <h3 className="stat-value">{dashboardStats.completed}</h3>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </>
            )}

            {/* Re-audits Section */}
            {reauditStats && (
              <>
                <Row className="g-4 mb-4">
                  <Col xs={12}>
                    <h5 className="section-title mb-3">
                      <FaRedo className="me-2" /> Re-Audits
                    </h5>
                  </Col>

                  {/* Total Re-Audits Card */}
                  <Col xs={12} sm={6} md={6} lg={3}>
                    <Card className="stat-card border-0 shadow-sm h-100" onClick={() => handleCardClick('all', 'reaudit')}>
                      <Card.Body>
                        <div className="d-flex align-items-center mb-3">
                          <div className="stat-icon reaudit-icon">
                            <FaRedo />
                          </div>
                          <h5 className="stat-label ms-3 mb-0">Total Re-Audits</h5>
                        </div>
                        <h3 className="stat-value">{reauditStats.total}</h3>
                      </Card.Body>
                    </Card>
                  </Col>

                  {/* Pending Re-Audits Card */}
                  <Col xs={12} sm={6} md={6} lg={3}>
                    <Card className="stat-card border-0 shadow-sm h-100" onClick={() => handleCardClick('pending', 'reaudit')}>
                      <Card.Body>
                        <div className="d-flex align-items-center mb-3">
                          <div className="stat-icon reaudit-icon">
                            <FaHourglassHalf />
                          </div>
                          <h5 className="stat-label ms-3 mb-0">Pending</h5>
                        </div>
                        <h3 className="stat-value">{reauditStats.pending}</h3>
                      </Card.Body>
                    </Card>
                  </Col>

                  {/* In Progress Re-Audits Card */}
                  <Col xs={12} sm={6} md={6} lg={3}>
                    <Card className="stat-card border-0 shadow-sm h-100" onClick={() => handleCardClick('inProgress', 'reaudit')}>
                      <Card.Body>
                        <div className="d-flex align-items-center mb-3">
                          <div className="stat-icon reaudit-icon">
                            <FaSpinner />
                          </div>
                          <h5 className="stat-label ms-3 mb-0">In Progress</h5>
                        </div>
                        <h3 className="stat-value">{reauditStats.inProgress}</h3>
                      </Card.Body>
                    </Card>
                  </Col>

                  {/* Completed Re-Audits Card */}
                  <Col xs={12} sm={6} md={6} lg={3}>
                    <Card className="stat-card border-0 shadow-sm h-100" onClick={() => handleCardClick('completed', 'reaudit')}>
                      <Card.Body>
                        <div className="d-flex align-items-center mb-3">
                          <div className="stat-icon reaudit-icon">
                            <FaCheckCircle />
                          </div>
                          <h5 className="stat-label ms-3 mb-0">Completed</h5>
                        </div>
                        <h3 className="stat-value">{reauditStats.completed}</h3>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </>
            )}

            {/* Audit Progress Analytics Section */}
            <DuserAuditProgress />
          </>
        )}
      </Container>

      <style>
        {`
          /* Dashboard Header Styles */
          .dashboard-header {
            background-image: linear-gradient(135deg, var(--bs-primary) 0%, #4361ee 100%);
            border-bottom-left-radius: 20px;
            border-bottom-right-radius: 20px;
            box-shadow: 0 4px 12px rgba(var(--bs-primary-rgb), 0.1);
          }
          
          .greeting {
            font-weight: 600;
          }
          
          .avatar-container {
            position: relative;
          }
          
          .department-avatar {
            width: 48px;
            height: 48px;
            background-color: rgba(255, 255, 255, 0.2);
            border: 2px solid rgba(255, 255, 255, 0.5);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: bold;
            font-size: 18px;
            color: white;
          }
          
          .department-avatar-img {
            width: 48px;
            height: 48px;
            border: 2px solid rgba(255, 255, 255, 0.5);
            border-radius: 50%;
            object-fit: cover;
            display: block;
          }
          
          .stats-badge {
            padding: 8px 16px;
            font-weight: 500;
            border-radius: 30px;
          }
          
          /* Dashboard Content Styles */
          .dashboard-content {
            background-color: #f8f9fa;
            min-height: calc(100vh - 82px);
          }
          
          .section-title {
            font-weight: 600;
            color: #344767;
            display: flex;
            align-items: center;
          }
          
          /* Stat Cards */
          .stat-card {
            border-radius: 12px;
            overflow: hidden;
            transition: all 0.3s ease;
            cursor: pointer;
          }
          
          .stat-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important;
          }
          
          .stat-icon {
            width: 45px;
            height: 45px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 20px;
          }
          
          .total-icon {
            background-color: rgba(13, 110, 253, 0.1);
            color: #0d6efd;
          }
          
          .pending-icon {
            background-color: rgba(255, 193, 7, 0.1);
            color: #ffc107;
          }
          
          .progress-icon {
            background-color: rgba(13, 110, 253, 0.1);
            color: #0d6efd;
          }
          
          .completed-icon {
            background-color: rgba(25, 135, 84, 0.1);
            color: #198754;
          }
          
          .reaudit-icon {
            background-color: rgba(220, 53, 69, 0.1);
            color: #dc3545;
          }
          
          .audit-icon {
            background-color: rgba(111, 66, 193, 0.1);
            color: #6f42c1;
          }
          
          .stat-percentage {
            font-weight: 600;
            font-size: 14px;
            padding: 4px 8px;
            border-radius: 20px;
          }
          
          .stat-value {
            font-weight: 700;
            font-size: 32px;
            margin: 10px 0;
            text-align: center;
          }
          
          .stat-label {
            font-size: 14px;
            font-weight: 500;
          }
          
          .stat-link {
            color: var(--bs-primary);
            font-weight: 500;
            font-size: 14px;
            text-decoration: none;
            display: inline-block;
          }
          
          /* Summary Card */
          .summary-card {
            border-radius: 12px;
          }
          
          .total-container {
            padding: 15px 0;
          }
          
          .total-circle {
            width: 100px;
            height: 100px;
            border-radius: 50%;
            background: linear-gradient(135deg, #4361ee, var(--bs-primary));
            color: white;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 28px;
            font-weight: 700;
            margin: 0 auto;
            box-shadow: 0 5px 15px rgba(67, 97, 238, 0.3);
          }
          
          .summary-indicator {
            width: 12px;
            height: 12px;
            border-radius: 50%;
            margin-right: 10px;
          }
          
          .summary-indicator.pending {
            background-color: #ffc107;
          }
          
          .summary-indicator.progress {
            background-color: #0d6efd;
          }
          
          .summary-indicator.completed {
            background-color: #198754;
          }
          
          .summary-text {
            font-weight: 500;
          }
          
          .summary-count {
            font-weight: 600;
          }
          
          .summary-percentage {
            font-size: 12px;
            font-weight: 500;
            padding: 2px 6px;
            border-radius: 10px;
            background-color: #f0f2f5;
          }
          
          /* Quick Action Cards */
          .quick-action-card {
            display: flex;
            align-items: center;
            padding: 1.25rem;
            border-radius: 12px;
            background-color: #fff;
            box-shadow: 0 2px 12px rgba(0,0,0,0.05);
            transition: all 0.3s ease;
            text-decoration: none;
            color: inherit;
            border: 1px solid #e9ecef;
            height: 100%;
          }
          
          .quick-action-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 10px 20px rgba(0,0,0,0.1);
          }
          
          .quick-action-icon {
            width: 60px;
            height: 60px;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-right: 1rem;
            flex-shrink: 0;
          }
          
          .audit-card .quick-action-icon {
            background-color: rgba(13, 110, 253, 0.1);
            color: #0d6efd;
          }
          
          .reaudit-card .quick-action-icon {
            background-color: rgba(220, 53, 69, 0.1);
            color: #dc3545;
          }
          
          .quick-action-content h5 {
            color: #344767;
            font-weight: 600;
          }
          
          /* Responsive Adjustments */
          @media (max-width: 992px) {
            .progress-stat-group {
              width: 100%;
              margin-top: 2rem;
            }
          }
          
          @media (max-width: 768px) {
            .total-circle {
              width: 80px;
              height: 80px;
              font-size: 22px;
            }
          }
          
          @media (max-width: 576px) {
            .dashboard-header {
              border-radius: 0;
            }
            
            .greeting {
              font-size: 1.25rem;
            }
            
            .quick-action-card {
              flex-direction: column;
              text-align: center;
            }
            
            .quick-action-icon {
              margin-right: 0;
              margin-bottom: 1rem;
            }
          }
        `}
      </style>
    </Container>
  );
}

export default JBranchDuserDashboard;
